import { useState, useEffect } from 'react';
import { Button, Alert, Row, Col } from 'reactstrap';
import { useGetRiskAssessmentByNumberQuery, useUpdateRiskAssessmentPermitMutation } from 'slices/riskAssements/riskAssementsSlice';
import { useNavigate } from "react-router-dom";
import { toast } from 'react-toastify';

interface RiskAssessmentSubmitProps {
    reportId: string | null; // or string if your IDs are strings
    location?: {
        latitude: number | null;
        longitude: number | null;
    };
}


const RiskAssessmentSubmit: React.FC<RiskAssessmentSubmitProps> = ({
    reportId,
    location = { latitude: '', longitude: '' }
}) => {
    const [isFormComplete, setIsFormComplete] = useState(false);
    const [validationErrors, setValidationErrors] = useState<string[]>([]);
    const navigate = useNavigate();

    const {
        data: reportData,
        isLoading,
        refetch: reportDataRefetch,
    } = useGetRiskAssessmentByNumberQuery(reportId || 0, { skip: !reportId });

    const [updateReport, { isLoading: isUpdating }] = useUpdateRiskAssessmentPermitMutation();


    // Validate form completeness whenever reportData changes
    useEffect(() => {
        if (reportData) {
            validateForm();
        }
    }, [reportData]);

    const validateForm = () => {
        const errors: string[] = [];


        if (!reportData?.location && (!reportData?.latitude || !reportData?.longitude)) {
            errors.push('Location is required');
        }

        // Questionnaires validation
        if (!reportData?.end_of_shift_questionnaire || reportData.end_of_shift_questionnaire.length === 0) {
            errors.push('End of shift questionnaire is incomplete');
        }
        if (!reportData?.checklist_questionnaire || reportData.checklist_questionnaire.length === 0) {
            errors.push('Checklist questionnaire is incomplete');
        }

        // Risk migration validation
        if (!reportData?.risk_migration || reportData.risk_migration.length === 0) {
            errors.push('At least one risk mitigation is required');
        }

        // Observation validation
        if (!reportData?.observation || reportData.observation.length === 0) {
            errors.push('At least one observation is required');
        } else {
            const incompleteObservation = reportData.observation.some(
                (obs: { observation: any; action_required: any; }) => !obs.observation || !obs.action_required
            );
            if (incompleteObservation) {
                errors.push('All observations must have both description and action required');
            }
        }

        setValidationErrors(errors);
        setIsFormComplete(errors.length === 0);
    };

    const handleSubmit = async () => {
        if (!isFormComplete) {
            return;
        }

        try {
            const payload = {
                latitude: reportData?.latitude || location.latitude,
                longitude: reportData?.longitude || location.longitude,
                report_status: 2 // Assuming 2 is the status for "Submitted"
            };

            await updateReport({
                assessment_id: reportId,
                updatedPermitData: payload,
            }).unwrap();

            reportDataRefetch();
            navigate('/risk-assessment-list');
            toast.success('Risk assessment submitted successfully!');
        } catch (error) {
            console.error('Submission error:', error);
            toast.error('Failed to submit risk assessment');
        }
    };

    if (isLoading) return <div>Loading...</div>;

    return (
        <div className="mt-4">
            <Row>
                <Col md={12}>
                {validationErrors?.length > 0 && (
                        <Alert color="danger">
                            <h5>Please complete the following before submitting:</h5>
                            <ul>
                                {validationErrors?.map((error, index) => (
                                    <li key={index}>{error}</li>
                                ))}
                            </ul>
                        </Alert>
                    )}
                </Col>
            </Row>
            <div className="mt-4 mb-3 d-flex justify-content-end">
                    <div className="mt-3 text-muted">
                        {isFormComplete ? (
                            <span className="text-success">All required information has been completed</span>
                        ) : (
                            <span className="text-danger">Please complete all sections before submitting</span>
                        )}
                    </div>&nbsp;&nbsp;

                    <Button
                        color="primary"
                        size="lg"
                        onClick={handleSubmit}
                        disabled={!isFormComplete || isLoading || isUpdating}
                    >
                        {isUpdating ? 'Submitting...' : 'Submit'}
                    </Button>

                    <Button
                        color="danger"
                        className="mx-2"
                        disabled={isLoading || isUpdating}
                        onClick={() => navigate(`/risk-assessment-list`)}
                    >
                        Cancel
                    </Button>
            </div>

        </div>
    );
};

export default RiskAssessmentSubmit;